import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type {
  PurchaseOrder,
  POStatus,
  POPriority,
  FreightType,
} from "serviceStores/mock/purchase-orders.mock";
import type { PurchaseOrderLineItem } from "serviceStores/mock/purchase-order-line-items.mock";
import type { Plant } from "serviceStores/mock/plants.mock";
import type { GLAccount } from "serviceStores/mock/gl-accounts.mock";
import type { FreightCarrier } from "serviceStores/mock/freight-carriers.mock";
import type { UploadedFile } from "components/DocumentUploader/DocumentUploader";

/**
 * Line item for the PO creation form
 */
interface POLineItem {
  tempId: string;
  itemCode: string;
  description: string;
  specifications?: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  extendedPrice: number;
  glAccountId: number;
  glAccountNumber?: string;
  notes?: string;
}

/**
 * Form data for PO creation
 */
interface POCreationFormData {
  // Step 1: Basic Info
  vendorId: number | null;
  vendorName?: string;
  jobId: number | null;
  jobNumber?: string;
  plantId: number | null;
  plantCode?: string;
  quoteAmount: number;
  expectedDeliveryDate: string;
  priority: POPriority;
  notes?: string;
  specialInstructions?: string;

  // Step 2: Line Items
  lineItems: POLineItem[];

  // Step 3: Freight
  freightType: FreightType;
  freightCarrierId: number | null;
  freightCarrierName?: string;
  freightAmount: number;
  freightInvoiceNumber?: string;

  // Step 4: Documents
  documents: UploadedFile[];
}

/**
 * Initial form data
 */
const initialFormData: POCreationFormData = {
  vendorId: null,
  jobId: null,
  plantId: null,
  quoteAmount: 0,
  expectedDeliveryDate: "",
  priority: "medium",
  notes: "",
  specialInstructions: "",
  lineItems: [],
  freightType: "none",
  freightCarrierId: null,
  freightAmount: 0,
  freightInvoiceNumber: "",
  documents: [],
};

/**
 * PO Creation Wizard Store
 *
 * Manages state for the Purchase Order creation wizard.
 * Follows the pattern established in PORequisitionWizardStore.
 */
class POCreationWizardStore {
  rootStore: RootStore;

  // Step navigation
  activeStep = 0;
  readonly steps = [
    "PO Details",
    "Line Items",
    "Freight",
    "Documents",
    "Review & Submit",
  ];

  // UI State
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: POCreationFormData = { ...initialFormData };

  // Reference Data (loaded from stores)
  vendors: Array<{ vendorId: number; name: string }> = [];
  jobs: Array<{ id: number; jobNumber: string; name: string }> = [];
  plants: Plant[] = [];
  glAccounts: GLAccount[] = [];
  freightCarriers: FreightCarrier[] = [];

  // Line item editing
  editingLineItemIndex: number | null = null;
  showLineItemDialog = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Step Navigation ====================

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  canProceed = (): boolean => {
    return this.validateCurrentStep(false);
  };

  // ==================== Form Data Updates ====================

  updateFormData = <K extends keyof POCreationFormData>(
    field: K,
    value: POCreationFormData[K]
  ) => {
    this.formData[field] = value;

    // Auto-update related fields
    if (field === "vendorId" && value) {
      const vendor = this.vendors.find((v) => v.vendorId === value);
      this.formData.vendorName = vendor?.name;
    }

    if (field === "plantId" && value) {
      const plant = this.plants.find((p) => p.plantId === value);
      this.formData.plantCode = plant?.plantCode;
    }

    if (field === "freightCarrierId" && value) {
      const carrier = this.freightCarriers.find((c) => c.carrierId === value);
      this.formData.freightCarrierName = carrier?.name;
    }

    if (field === "lineItems") {
      this.recalculateTotals();
    }
  };

  // ==================== Line Item Management ====================

  addLineItem = (item: Omit<POLineItem, "tempId" | "extendedPrice">) => {
    const newItem: POLineItem = {
      ...item,
      tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      extendedPrice: item.quantity * item.unitPrice,
    };
    this.formData.lineItems.push(newItem);
    this.recalculateTotals();
  };

  updateLineItem = (
    index: number,
    item: Partial<Omit<POLineItem, "tempId">>
  ) => {
    if (index >= 0 && index < this.formData.lineItems.length) {
      this.formData.lineItems[index] = {
        ...this.formData.lineItems[index],
        ...item,
        extendedPrice:
          (item.quantity ?? this.formData.lineItems[index].quantity) *
          (item.unitPrice ?? this.formData.lineItems[index].unitPrice),
      };
      this.recalculateTotals();
    }
  };

  removeLineItem = (index: number) => {
    if (index >= 0 && index < this.formData.lineItems.length) {
      this.formData.lineItems.splice(index, 1);
      this.recalculateTotals();
    }
  };

  openLineItemDialog = (index?: number) => {
    this.editingLineItemIndex = index ?? null;
    this.showLineItemDialog = true;
  };

  closeLineItemDialog = () => {
    this.editingLineItemIndex = null;
    this.showLineItemDialog = false;
  };

  // ==================== Document Management ====================

  addDocuments = (files: UploadedFile[]) => {
    this.formData.documents = [...this.formData.documents, ...files];
  };

  removeDocument = (fileId: string) => {
    this.formData.documents = this.formData.documents.filter(
      (d) => d.id !== fileId
    );
  };

  updateDocumentCategory = (
    fileId: string,
    category: UploadedFile["category"]
  ) => {
    const doc = this.formData.documents.find((d) => d.id === fileId);
    if (doc) {
      doc.category = category;
    }
  };

  // ==================== Calculations ====================

  recalculateTotals = () => {
    // Line items total is handled by computed getter
  };

  get lineItemsTotal(): number {
    return this.formData.lineItems.reduce(
      (sum, item) => sum + item.extendedPrice,
      0
    );
  }

  get taxAmount(): number {
    // 8% tax rate (configurable)
    return Math.round(this.lineItemsTotal * 0.08 * 100) / 100;
  }

  get totalAmount(): number {
    return this.lineItemsTotal + this.taxAmount + this.formData.freightAmount;
  }

  get quoteVariance(): number {
    return this.lineItemsTotal - this.formData.quoteAmount;
  }

  get hasQuoteVariance(): boolean {
    return Math.abs(this.quoteVariance) > 0.01;
  }

  // ==================== Validation ====================

  validateCurrentStep = (showError = true): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0: // PO Details
        if (!this.formData.vendorId) {
          if (showError) this.error = "Please select a vendor";
          return false;
        }
        if (!this.formData.plantId) {
          if (showError) this.error = "Please select a plant";
          return false;
        }
        if (this.formData.quoteAmount <= 0) {
          if (showError) this.error = "Please enter the quote amount";
          return false;
        }
        if (!this.formData.expectedDeliveryDate) {
          if (showError) this.error = "Please enter the expected delivery date";
          return false;
        }
        return true;

      case 1: // Line Items
        if (this.formData.lineItems.length === 0) {
          if (showError) this.error = "Please add at least one line item";
          return false;
        }
        const hasInvalidItems = this.formData.lineItems.some(
          (item) =>
            !item.description ||
            item.quantity <= 0 ||
            !item.glAccountId
        );
        if (hasInvalidItems) {
          if (showError)
            this.error = "Please complete all required line item fields";
          return false;
        }
        return true;

      case 2: // Freight
        if (
          this.formData.freightType === "third_party" &&
          !this.formData.freightCarrierId
        ) {
          if (showError)
            this.error = "Please select a freight carrier for third-party freight";
          return false;
        }
        if (
          this.formData.freightType !== "none" &&
          this.formData.freightAmount <= 0
        ) {
          if (showError) this.error = "Please enter the freight amount";
          return false;
        }
        return true;

      case 3: // Documents
        // Documents are optional
        return true;

      case 4: // Review
        return true;

      default:
        return true;
    }
  };

  // ==================== Data Loading ====================

  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load vendors from CRM store
      if ((this.rootStore as any).vendorsStore) {
        await (this.rootStore as any).vendorsStore.find({
          query: { isActive: true, $limit: 500 },
        });
      }

      // Load plants from mock store
      if ((this.rootStore as any).plantsStore) {
        await (this.rootStore as any).plantsStore.find();
      }

      // Load GL accounts from mock store
      if ((this.rootStore as any).glAccountsStore) {
        await (this.rootStore as any).glAccountsStore.find({
          query: { isActive: true },
        });
      }

      // Load freight carriers from mock store
      if ((this.rootStore as any).freightCarriersStore) {
        await (this.rootStore as any).freightCarriersStore.find({
          query: { isActive: true },
        });
      }

      // Load jobs if available
      if ((this.rootStore as any).projectsStore) {
        await (this.rootStore as any).projectsStore.find({
          query: { $limit: 100 },
        });
      }

      runInAction(() => {
        // Reference from stores
        this.vendors = ((this.rootStore as any).vendorsStore?.entities || []).map(
          (v: any) => ({
            vendorId: v.vendorId || v.id,
            name: v.name,
          })
        );
        this.plants = (this.rootStore as any).plantsStore?.entities || [];
        this.glAccounts = (this.rootStore as any).glAccountsStore?.entities || [];
        this.freightCarriers =
          (this.rootStore as any).freightCarriersStore?.entities || [];
        this.jobs = ((this.rootStore as any).projectsStore?.entities || []).map(
          (j: any) => ({
            id: j.projectId || j.id,
            jobNumber: j.projectNumber || j.jobNumber || `J-${j.id}`,
            name: j.name,
          })
        );

        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  // ==================== Submission ====================

  generatePONumber = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `PO-${year}${month}${day}-${random}`;
  };

  submitPO = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const poData: Partial<PurchaseOrder> = {
        poNumber: this.generatePONumber(),
        vendorId: this.formData.vendorId!,
        vendorName: this.formData.vendorName,
        jobId: this.formData.jobId || undefined,
        jobNumber: this.formData.jobNumber,
        plantId: this.formData.plantId!,
        plantCode: this.formData.plantCode,
        requestedBy: (this.rootStore as any).authStore?.user?.id || 1,
        requestedByName:
          (this.rootStore as any).authStore?.user?.name || "Current User",
        quoteAmount: this.formData.quoteAmount,
        totalAmount: this.totalAmount,
        taxAmount: this.taxAmount,
        freightAmount: this.formData.freightAmount,
        currency: "USD",
        freightType: this.formData.freightType,
        freightCarrierId: this.formData.freightCarrierId || undefined,
        freightCarrierName: this.formData.freightCarrierName,
        freightInvoiceNumber: this.formData.freightInvoiceNumber || undefined,
        orderDate: new Date().toISOString().split("T")[0],
        expectedDeliveryDate: this.formData.expectedDeliveryDate,
        status: "pending_receiving",
        priority: this.formData.priority,
        deliveryLocation: `Plant ${this.formData.plantCode}`,
        hasQuoteDocument: this.formData.documents.some(
          (d) => d.category === "quote"
        ),
        hasPackingSlip: false,
        hasInvoice: false,
        hasFreightBill: false,
        notes: this.formData.notes,
        specialInstructions: this.formData.specialInstructions,
      };

      // Create PO
      const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore;
      if (purchaseOrdersStore) {
        const createdPO = await purchaseOrdersStore.create(poData);

        // Create line items
        const lineItemsStore = (this.rootStore as any)
          .purchaseOrderLineItemsStore;
        if (lineItemsStore && createdPO) {
          for (let i = 0; i < this.formData.lineItems.length; i++) {
            const item = this.formData.lineItems[i];
            await lineItemsStore.create({
              purchaseOrderId: createdPO.purchaseOrderId,
              lineNumber: i + 1,
              itemCode: item.itemCode,
              description: item.description,
              specifications: item.specifications,
              quantity: item.quantity,
              receivedQuantity: 0,
              unitOfMeasure: item.unitOfMeasure,
              unitPrice: item.unitPrice,
              extendedPrice: item.extendedPrice,
              glAccountId: item.glAccountId,
              glAccountNumber: item.glAccountNumber,
              status: "ordered",
              notes: item.notes,
            });
          }
        }

        // Create task in approval queue for receiving
        const approvalQueueStore = (this.rootStore as any).approvalQueueStore;
        if (approvalQueueStore && createdPO) {
          await approvalQueueStore.create({
            queueType: "receiving",
            entityType: "purchase_order",
            entityId: createdPO.purchaseOrderId,
            entityNumber: createdPO.poNumber,
            plantId: this.formData.plantId,
            plantCode: this.formData.plantCode,
            title: `Receive PO ${createdPO.poNumber}`,
            description: `${this.formData.vendorName} - Expected ${this.formData.expectedDeliveryDate}`,
            priority: this.formData.priority === "urgent" ? "urgent" : "normal",
            status: "pending",
            createdDate: new Date().toISOString().split("T")[0],
            dueDate: this.formData.expectedDeliveryDate,
            vendorName: this.formData.vendorName,
            totalAmount: this.totalAmount,
          });
        }

        runInAction(() => {
          this.success = `Purchase Order ${createdPO.poNumber} created successfully!`;
          this.loading = false;

          // Navigate to receiving queue after delay
          setTimeout(() => {
            this.reset();
            this.rootStore.routerStore.goTo("ReceivingQueue");
          }, 2000);
        });
      } else {
        // Mock mode - simulate success
        runInAction(() => {
          this.success = `Purchase Order ${this.generatePONumber()} created successfully! (Mock Mode)`;
          this.loading = false;

          setTimeout(() => {
            this.reset();
            this.rootStore.routerStore.goTo("Dashboard");
          }, 2000);
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to create purchase order";
        this.loading = false;
      });
    }
  };

  // ==================== Reset ====================

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = { ...initialFormData, lineItems: [], documents: [] };
    this.editingLineItemIndex = null;
    this.showLineItemDialog = false;
  };
}

export default POCreationWizardStore;
